import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { AdyenDocumentStateRow } from "../../../../interfaces/adyenDocumentState.interface";
import {
  getAdyenDocumentStateThunk,
  selectAdyenDocumentsBusinessId,
  selectAdyenDocumentsError,
  selectCurrentBusinessAdyenDocuments,
} from "../../businessesSlice";

/** Stable identity, so a consumer can memoise on `rows`. */
const EMPTY_ROWS: AdyenDocumentStateRow[] = [];

/**
 * Adyen document state for one business, fetched once per business even though
 * the Files tab and every person row mount this hook.
 */
export function useAdyenDocumentState(businessId?: string) {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectCurrentBusinessAdyenDocuments);
  const error = useAppSelector(selectAdyenDocumentsError);
  const loadedBusinessId = useAppSelector(selectAdyenDocumentsBusinessId);

  // Both dispatches are de-duplicated by the thunk's own condition.
  const refetch = useCallback(() => {
    if (!businessId) return;
    dispatch(getAdyenDocumentStateThunk({ businessId, force: true }));
  }, [businessId, dispatch]);

  useEffect(() => {
    if (!businessId) return;
    dispatch(getAdyenDocumentStateThunk({ businessId }));
  }, [businessId, dispatch]);

  const isForCurrentBusiness = Boolean(
    businessId && businessId === loadedBusinessId
  );

  return {
    rows: isForCurrentBusiness ? rows : EMPTY_ROWS,
    error: isForCurrentBusiness ? error : null,
    refetch,
  };
}
